import {
  Operator,
  type APIDashboardFilterExpression,
  type DashboardFilterValues,
} from '@ardoq/api-types';

type CompactDashboardFilterExpression = [
  operator: APIDashboardFilterExpression['operator'],
  value: APIDashboardFilterExpression['value'],
  label?: string,
];

type CompactDashboardFilterValues = [
  parameterId: string,
  expressions: CompactDashboardFilterExpression[],
][];

const operators = new Set<unknown>(Object.values(Operator));

const isDashboardFilterValue = (
  value: unknown
): value is APIDashboardFilterExpression['value'] =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'boolean' ||
  typeof value === 'number';

const isCompactDashboardFilterExpression = (
  expression: unknown
): expression is CompactDashboardFilterExpression =>
  Array.isArray(expression) &&
  (expression.length === 2 || expression.length === 3) &&
  operators.has(expression[0]) &&
  isDashboardFilterValue(expression[1]) &&
  (expression.length === 2 || typeof expression[2] === 'string');

const isCompactDashboardFilterEntry = (
  entry: unknown
): entry is CompactDashboardFilterValues[number] =>
  Array.isArray(entry) &&
  entry.length === 2 &&
  typeof entry[0] === 'string' &&
  Array.isArray(entry[1]) &&
  entry[1].every(isCompactDashboardFilterExpression);

const toCompactExpression = ({
  operator,
  value,
  label,
}: APIDashboardFilterExpression): CompactDashboardFilterExpression =>
  label === undefined ? [operator, value] : [operator, value, label];

const fromCompactExpression = ([
  operator,
  value,
  label,
]: CompactDashboardFilterExpression): APIDashboardFilterExpression =>
  label === undefined ? { operator, value } : { operator, value, label };

const toCompactDashboardFilterValues = (
  dashboardFilterValues: DashboardFilterValues
): CompactDashboardFilterValues =>
  Object.entries(dashboardFilterValues).map(([parameterId, expressions]) => [
    parameterId,
    expressions.map(toCompactExpression),
  ]);

const fromCompactDashboardFilterValues = (
  dashboardFilterValues: CompactDashboardFilterValues
): DashboardFilterValues =>
  Object.fromEntries(
    dashboardFilterValues.map(([parameterId, expressions]) => [
      parameterId,
      expressions.map(fromCompactExpression),
    ])
  );

const fromString = (value: string): DashboardFilterValues | undefined => {
  try {
    const parsed: unknown = JSON.parse(value);

    return Array.isArray(parsed) && parsed.every(isCompactDashboardFilterEntry)
      ? fromCompactDashboardFilterValues(parsed)
      : undefined;
  } catch {
    return undefined;
  }
};

const toString = (value: DashboardFilterValues): string =>
  JSON.stringify(toCompactDashboardFilterValues(value));

export const dashboardFilterValuesParam = { fromString, toString };
